import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faHeadset, faStar } from "@fortawesome/free-solid-svg-icons";
import { Button, Input } from "antd";
import ConditionalLayout from "@/Components/ConditionalLayout";
import { useSelector } from "react-redux";
import { getUserDetailsFromReducer } from "Store/reducers/Auth/AuthSlice";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import { useNavigate, useSearchParams } from "react-router-dom";

const ContactSupport = () => {
  const [message, setMessage] = useState("");
  const user = useSelector(getUserDetailsFromReducer);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // WhatsApp configuration
  const whatsappNumber = "+919426320180"; // Replace with your actual WhatsApp number

  // Default message template
  const defaultMessage =
    "Hi! I need help with my InProfile business account. Can you please assist me?";

  // Handle URL parameters for prefilled messages
  useEffect(() => {
    const featureKey = searchParams.get("feature");
    const featureName = searchParams.get("featureName");

    if (featureKey && featureName) {
      const prefilledMessage = `Hi! I'm interested in accessing the "${featureName}" feature on InProfile. I don't currently have access to this premium feature and would like to know more about how to unlock it. Could you please provide me with information about pricing, features included, and how to upgrade my account? I'm looking forward to using this feature for my business.`;
      setMessage(prefilledMessage);
    }
  }, [searchParams]);

  // Get user info for dynamic message
  const getUserInfo = () => {
    if (user?.username && user?.email) {
      return `\n\nUser Details:\nUsername: ${user.username}\nEmail: ${user.email}`;
    }
    return "";
  };

  // Create final message with user info
  const getFinalMessage = () => {
    const userMessage = message || defaultMessage;
    const userInfo = getUserInfo();
    return userMessage + userInfo;
  };

  const handleWhatsAppClick = () => {
    const finalMessage = getFinalMessage();
    const encodedMessage = encodeURIComponent(finalMessage);
    const whatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
    window.open(whatsappUrl, "_blank");
  };

  const handleLoginClick = () => {
    navigate("/login");
  };

  return (
    <ConditionalLayout>
      <div className="h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex flex-col">
        {/* Main Content */}
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="max-w-xl w-full">
            <div className="bg-white rounded-2xl shadow-xl p-6 text-center border border-gray-100">
              {/* Branded Header */}
              <div className="mb-4">
                <div className="flex justify-center mb-3">
                  <div className="relative">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center shadow-lg">
                      <FontAwesomeIcon
                        icon={faHeadset}
                        className="text-white text-xl"
                      />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                      <FontAwesomeIcon
                        icon={faStar}
                        className="text-white text-xs"
                      />
                    </div>
                  </div>
                </div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Contact Support
                </h1>
                <p className="text-gray-600 text-sm">
                  Need help with your InProfile business account? We're here to
                  assist you via WhatsApp.
                </p>
              </div>

              {/* WhatsApp Contact Card */}
              <div className="space-y-4">
                {/* Combined Query and Support Section */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4 shadow-sm">
                  <div className="flex items-center justify-center mb-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center shadow-md">
                      <FontAwesomeIcon
                        icon={faWhatsapp}
                        className="text-white text-lg"
                      />
                    </div>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 mb-1 text-center">
                    WhatsApp Support
                  </h3>
                  <p className="text-gray-600 mb-3 text-xs text-center">
                    Get instant help from our dedicated support team
                  </p>

                  {/* Query Input */}
                  <div className="mb-4">
                    {searchParams.get("feature") &&
                      searchParams.get("featureName") && (
                        <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded-lg">
                          <p className="text-xs text-blue-700">
                            💡 Message prefilled for feature access inquiry
                          </p>
                        </div>
                      )}
                    <h4 className="text-xs font-medium text-gray-900 mb-2 flex items-center">
                      <FontAwesomeIcon
                        icon={faStar}
                        className="text-yellow-500 mr-2 text-xs"
                      />
                      Tell us about your query:
                    </h4>
                    <div className="space-y-2">
                      <Input.TextArea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Describe your issue or question here..."
                        rows={3}
                        // className="rounded-lg border-2 border-green-200 focus:border-green-400 text-sm"
                      />
                      <p className="text-xs text-gray-500 text-left">
                        Your message will be sent to our support team along with
                        your account details (if logged in).
                      </p>
                    </div>
                  </div>

                  <Button
                    type="primary"
                    size="large"
                    className="w-full bg-green-600 hover:bg-green-700 border-green-600 hover:border-green-700 rounded-lg h-10 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200"
                    icon={
                      <FontAwesomeIcon icon={faWhatsapp} className="mr-2" />
                    }
                    onClick={handleWhatsAppClick}
                  >
                    Start WhatsApp Chat
                  </Button>

                  {/* Login Button - Only show if user is not logged in */}
                  {!user.username && (
                    <Button
                      type="primary"
                      size="large"
                      className="w-full mt-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 border-0 rounded-lg h-10 text-white font-medium shadow-md hover:shadow-lg transition-all duration-200"
                      onClick={handleLoginClick}
                    >
                      Login to Your Account
                    </Button>
                  )}
                </div>
              </div>

              {/* Branded Footer */}
              <div className="mt-4 pt-3 border-t border-gray-200">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                  <p className="text-xs text-gray-500">
                    We typically respond within 1-2 hours during business hours
                  </p>
                  <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                </div>
                <div className="flex items-center justify-center space-x-4 text-xs text-gray-400">
                  <span>Powered by InProfile</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Branding */}
        <div className="bg-white border-t border-gray-100 py-2 flex-shrink-0">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center space-x-6 text-xs text-gray-500">
              <span>© 2025 InProfile. All rights reserved.</span>
              <span>•</span>
              <span>Designed in India. Built for Everyone.</span>
            </div>
          </div>
        </div>
      </div>
    </ConditionalLayout>
  );
};

export default ContactSupport;
